import { type FC, type ReactNode, useEffect, useState } from "react";
import SelectDatePopup from "./SelectDatePopup";
import { DateFormatType, formatDate, getPattern } from "~/common/utils/DateFormatter";
import { useBooleanState } from "../hooks/useBooleanState";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { getFirstValidDate, getNextValidDeliveryDate } from "~/common/utils/dateUtils";
import { type TextFieldVariants } from "~/common/types";
import { IconButton, InputAdornment, TextField } from "~/common/mui";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import ErrorIcon from "@mui/icons-material/Error";

dayjs.extend(customParseFormat);

export interface DateInputProps {
  className?: string;
  date: Date | null;
  onChange?: (newDate: Date) => void;
  onClear?: () => void;
  showClear?: boolean;
  showErrorIcon?: boolean;
  onBlur?: (newDate?: Date) => void;
  onFocus?: () => void;
  minYear?: number;
  maxYear?: number;
  label?: ReactNode;
  helperLabel?: ReactNode;
  isInvalid?: boolean;
  isHighlight?: boolean;
  isDayDisabled?: (date: Date) => boolean;
  disabled?: boolean;
  placeholder?: string;
  showValueAsPlaceholder?: boolean;
  "data-test-id"?: string;
  startAdornment?: ReactNode;
  variant?: TextFieldVariants;
  iconClassName?: string;
  size?: "small" | "medium";
}

const DEFAULT_PLACEHOLDER = getPattern(DateFormatType.DATE);

const DateInput: FC<DateInputProps> = (props) => {
  const {
    className,
    date,
    onChange,
    onBlur,
    onFocus,
    isInvalid,
    isHighlight,
    label,
    helperLabel,
    minYear,
    maxYear,
    isDayDisabled,
    disabled,
    showValueAsPlaceholder,
    "data-test-id": testId,
    showClear,
    showErrorIcon = false,
    onClear,
    ...rest
  } = props;
  const { val: modalOpen, setFalse: closeModal, setTrue: openModal } = useBooleanState(false);

  const placeholder = props.placeholder !== undefined ? props.placeholder : DEFAULT_PLACEHOLDER;

  const [inputText, setInputText] = useState(date ? formatDate(date, DateFormatType.DATE) : "");

  const adaptCloseToBlur = () => {
    closeModal();
    if (onBlur) {
      onBlur();
    }
  };

  useEffect(() => {
    setInputText(date ? formatDate(date, DateFormatType.DATE) : "");
  }, [date]);

  const blurField = () => {
    let newDate = undefined;
    try {
      newDate = dayjs(inputText, getPattern(DateFormatType.DATE));
    } catch (error) {
      console.warn("Failed to parse date");
      newDate = dayjs(getFirstValidDate());
    }
    if (!newDate.isValid()) {
      newDate = dayjs(getFirstValidDate());
    }

    if (onChange) {
      if (isDayDisabled && isDayDisabled(newDate.toDate())) {
        // force adjust date if incorect as in ACs
        // TODO: This should not be a concern of the date input. Move the logic to some validation layer
        onChange(getNextValidDeliveryDate(newDate).toDate());
      } else {
        // if a valid date, trigger on change
        onChange(newDate.toDate());
      }
    }
    // trigger with error date - because of the auto-adjust date, i can't trigger error in parent
    if (onBlur) {
      onBlur(newDate.toDate());
    }
  };

  return (
    <>
      <TextField
        className={className}
        data-test-id={testId}
        label={label}
        value={showValueAsPlaceholder ? "" : inputText}
        onChange={(event) => {
          // force field to typeable
          setInputText(event.target.value);
        }}
        onBlur={blurField}
        onFocus={onFocus}
        placeholder={showValueAsPlaceholder ? inputText : placeholder}
        error={isInvalid}
        highlight={isHighlight}
        helperText={helperLabel}
        fullWidth={true}
        disabled={disabled}
        {...rest}
        slotProps={{
          inputLabel: {
            shrink: true,
          },
          input: {
            sx: { paddingRight: "4px" },
            endAdornment: (
              <InputAdornment position="end">
                {showClear && (
                  <IconButton onClick={onClear} disabled={disabled}>
                    <ClearIcon />
                  </IconButton>
                )}
                {!showErrorIcon ? (
                  <IconButton onClick={openModal} disabled={disabled} color={"textSecondary"}>
                    <CalendarTodayOutlinedIcon />
                  </IconButton>
                ) : (
                  <IconButton onClick={openModal} disabled={disabled} color={"error"}>
                    <ErrorIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          },
        }}
      />
      {modalOpen && (
        <SelectDatePopup
          date={date}
          onSelect={onChange}
          onClose={adaptCloseToBlur}
          minYear={minYear}
          maxYear={maxYear}
          isDayDisabled={isDayDisabled}
        />
      )}
    </>
  );
};

export default DateInput;
